/**
 * A hairline (cockatrice-ui `src/ui/Divider.jsx`, ported to hyperscript; PLAN.md §0). `orientation`: "horizontal"
 * (default) | "vertical". Optional `label` ("or", "Older") sits in the middle. `strong` uses the border colour instead
 * of the divider colour.
 */
import './css/divider.css';
import { mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface DividerProps extends Attributes {
  /** Direction. Default "horizontal". */
  readonly orientation?: 'horizontal' | 'vertical' | undefined;
  /** Text in the middle (horizontal only). */
  readonly label?: Child;
  /** Use the border colour instead of the divider colour. */
  readonly strong?: boolean | undefined;
  /** Margin around the line. */
  readonly spacing?: 'sm' | 'md' | 'lg' | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function Divider(props: DividerProps): Child {
  const [local, rest] = splitProps(props, ['class', 'orientation', 'label', 'strong', 'spacing']);
  const vertical = (): boolean => local.orientation === 'vertical';
  return when(
    () => Boolean(local.label) && !vertical(),
    () =>
      el(
        'div',
        mergeProps(
          {
            role: 'separator',
            class: () =>
              ['ui-divider-label', local.strong === true && 'ui-divider--strong', local.spacing !== undefined && `ui-divider--space-${local.spacing}`, local.class]
                .filter(Boolean)
                .join(' '),
          },
          rest,
        ),
        el('span', {}, () => local.label),
      ),
    () =>
      el(
        'div',
        mergeProps(
          {
            role: 'separator',
            'aria-orientation': () => (vertical() ? 'vertical' : undefined),
            class: () =>
              [
                'ui-divider',
                vertical() && 'ui-divider--vertical',
                local.strong === true && 'ui-divider--strong',
                local.spacing !== undefined && `ui-divider--space-${local.spacing}`,
                local.class,
              ]
                .filter(Boolean)
                .join(' '),
          },
          rest,
        ),
      ),
  );
}
